import type { ComponentProps } from 'react';
import type React from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Separator } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { ChannelAwareCta } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { RoundShareIconButton, ShareChannelButton } from '#src/app/shared/components/share';

import { ChannelAwareCtaButton } from './components/ChannelAwareCtaButton';
import { MotivationCallout } from './components/MotivationCallout';
import { PetitionShareSlidePetitionCard } from './components/PetitionShareSlideUpHeader';
import {
	ACTION_BUTTON_TRACKING_DATA,
	CHANNEL_GRID_TRACKING_DATA,
	getShareIconOnBeforeShare,
	SHARE_ICON_SIZE_CLASSNAME,
} from './constants';
import type { PetitionShareButtonConfig, PetitionShareSlideUpProps } from './types';

const SpecialShareButtons = ({
	configs,
	variant,
}: {
	configs: readonly PetitionShareButtonConfig[];
	variant: ComponentProps<typeof Button>['variant'];
}) => (
	<div className="grid grid-cols-1 gap-x-0.5 gap-y-2" data-testid="petition-share-slide-up-special-buttons">
		{configs.map((buttonConfig) => (
			<ShareChannelButton
				key={buttonConfig.channel}
				buttonConfig={buttonConfig}
				variant={variant}
				trackingData={ACTION_BUTTON_TRACKING_DATA}
			/>
		))}
	</div>
);

/**
 * Splits the server-provided configs into the top "special" buttons and the remaining grid channels.
 * With a channel-aware CTA (the Top-8 relaunch experiments — CHANGE-99290) a "Share on <channel>"
 * button is promoted above the grid while the channel STAYS in the grid. Copy link placement differs
 * per layout:
 * - cta_with_secondary_copy: Copy link is pulled into the special row as a knockout secondary button below the CTA.
 * - cta_only: when the CTA renders, Copy link stays in the grid as an icon so the CTA is the sole
 *   special button; if the CTA can't render (channel missing from the share config), Copy link falls
 *   back to the primary special button so the sheet still has a prominent action.
 * - no CTA: Copy link is the primary special button (production sheet).
 *
 * Both layouts are live: the Top-8 relaunch runs one per treatment arm on both channels, so which one
 * a signer sees comes from their assigned arm rather than from the channel.
 */
function splitButtonConfigs(
	buttonConfigs: readonly PetitionShareButtonConfig[],
	channelAwareCta: ChannelAwareCta,
): {
	ctaButtonConfig: PetitionShareButtonConfig | undefined;
	specialButtonConfigs: PetitionShareButtonConfig[];
	regularButtonConfigs: PetitionShareButtonConfig[];
} {
	const ctaButtonConfig = channelAwareCta
		? buttonConfigs.find((config) => config.channel === channelAwareCta.channel)
		: undefined;
	const keepCopyInGrid = channelAwareCta?.layout === 'cta_only' && ctaButtonConfig !== undefined;
	const specialButtonConfigs: PetitionShareButtonConfig[] = [];
	const regularButtonConfigs = buttonConfigs.filter((config) => {
		if (config.channel === 'copy' && !keepCopyInGrid) {
			specialButtonConfigs.push(config);
			return false;
		}
		return true;
	});
	return { ctaButtonConfig, specialButtonConfigs, regularButtonConfigs };
}

const Footer = ({ continueButtonProps }: { continueButtonProps: PetitionShareSlideUpProps['continueButtonProps'] }) => {
	if (!continueButtonProps) return null;

	return (
		<ModalFooter className="justify-center" data-testid="petition-share-slide-up-footer" displayFooterTopSeparator>
			<Button
				data-qa="petition-share-slide-up-continue-button"
				variant="tertiary"
				className="w-full"
				onClick={continueButtonProps.action}
			>
				{continueButtonProps.label}
			</Button>
		</ModalFooter>
	);
};

/**
 * Extracted only to keep PetitionShareSlideUp's render within the max-lines-per-function budget.
 */
const ShareChannelsGrid = ({
	buttonConfigs,
	close,
	closeOnSocialShare,
}: {
	buttonConfigs: readonly PetitionShareButtonConfig[];
	close: () => void;
	closeOnSocialShare: boolean;
}) => (
	<div className="grid grid-cols-4 gap-x-0.5 gap-y-5">
		{buttonConfigs.map((buttonConfig) => (
			<RoundShareIconButton
				key={buttonConfig.channel}
				className={cn(SHARE_ICON_SIZE_CLASSNAME, 'w-full')}
				buttonConfig={buttonConfig}
				data-qa={`petition-share-slide-up-grid-${buttonConfig.channel}-button`}
				trackingData={CHANNEL_GRID_TRACKING_DATA}
				onBeforeShare={getShareIconOnBeforeShare({ channel: buttonConfig.channel, close, closeOnSocialShare })}
			/>
		))}
	</div>
);

// eslint-disable-next-line max-lines-per-function
export const PetitionShareSlideUp: React.FC<PetitionShareSlideUpProps> = ({
	petition,
	buttonConfigs,
	onClose = () => {},
	onOpenChange,
	continueButtonProps,
	isOpen,
	shareVisual,
	closeOnSocialShare = true,
	channelAwareCta = null,
	showMotivationCallout = false,
}) => {
	const { translate } = useI18n();

	const { ctaButtonConfig, specialButtonConfigs, regularButtonConfigs } = splitButtonConfigs(
		buttonConfigs,
		channelAwareCta,
	);
	// Demote Copy link only when the channel CTA actually renders, so a user whose share config lacks
	// the channel still gets a primary Copy link rather than a demoted one with no primary above it.
	const copyLinkVariant = ctaButtonConfig ? 'secondary' : 'primary';

	return (
		<Modal
			isOpen={isOpen}
			transformToSheet
			onClose={onClose}
			onOpenChange={onOpenChange}
			aria-label={translate('corgi.psf.promote_or_share.share_slide_up.aria_label')}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			className="z-100"
		>
			<ModalHeader title={shareVisual ? 'Share petition' : undefined} />

			{shareVisual && (
				<Separator className="border-stroke-weaker -mx-5 mt-4 mb-0 w-[calc(100%+2.5rem)] md:-mx-6 md:w-[calc(100%+3rem)]" />
			)}

			<ModalBody
				className={cn('max-h-[calc(85dvh-80px)] pb-6', { 'pt-0': shareVisual })}
				data-testid="petition-share-slide-up-body"
			>
				{({ close }) => (
					<div className="w-full">
						{shareVisual ? (
							<img src={shareVisual} alt="Share Visual Preview" className="mx-auto my-4 block max-w-61 rounded-md" />
						) : (
							<div className="mb-4">
								<PetitionShareSlidePetitionCard petition={petition} />
							</div>
						)}
						{(ctaButtonConfig || specialButtonConfigs.length > 0) && (
							<>
								<div className="flex flex-col gap-4">
									{channelAwareCta && ctaButtonConfig && (
										<ChannelAwareCtaButton
											channel={channelAwareCta.channel}
											buttonConfig={ctaButtonConfig}
											onBeforeShare={getShareIconOnBeforeShare({
												channel: channelAwareCta.channel,
												close,
												closeOnSocialShare,
											})}
										/>
									)}
									{specialButtonConfigs.length > 0 && (
										<SpecialShareButtons configs={specialButtonConfigs} variant={copyLinkVariant} />
									)}
								</div>
								<Separator className="border-stroke-weaker my-5" data-testid="petition-share-slide-up-top-separator" />
								{/* After the divider, before the channel grid (AC #16 / no_anchor when neither the Copy-link row nor the Facebook CTA renders). */}
								{showMotivationCallout && <MotivationCallout />}
							</>
						)}
						<ShareChannelsGrid
							buttonConfigs={regularButtonConfigs}
							close={close}
							closeOnSocialShare={closeOnSocialShare}
						/>
					</div>
				)}
			</ModalBody>
			<Footer continueButtonProps={continueButtonProps} />
		</Modal>
	);
};
